/**
 * Handing a module to the programme lead, with whatever it still carries.
 *
 * @module components/studio/SubmissionDialog
 */

import React, { useState } from "react";

import {
  Alert,
  Button,
  Checkbox,
  countLabel,
  Eyebrow,
  Heading,
  Modal,
  MutedText,
  Text,
} from "@/components/ui";
import { FormField, TextAreaField } from "@/components/ui/Form";
import type { ModuleWorkspaceRecord } from "@/platform";
import type { ModuleSubmissionWarningSection } from "@/server/types";

/** A warning's section by the tab it is fixed on, not the rule's key. */
const WARNING_SECTION_LABELS: Record<ModuleSubmissionWarningSection, string> = {
  details: "Details",
  mimlos: "Outcomes",
  effort: "Effort hours",
  assessments: "Assessments",
  mapping: "Mapping",
  reading: "Reading",
  "programme-context": "Programme context",
};

export interface SubmissionDialogProps {
  open: boolean;
  record: ModuleWorkspaceRecord;
  submitting: boolean;
  /** Why the module cannot be submitted now, such as a lease lost while open. */
  refusal?: string;
  error: Error | null;
  onClose: () => void;
  onSubmit: (options: { note: string; warningsAcknowledged: boolean }) => void;
}

/** Why Submit refuses, the save in flight first and the warnings last. */
function confirmRefusal(
  inFlight: string | undefined,
  refusal: string | undefined,
  unacknowledged: boolean,
): string | undefined {
  if (inFlight || refusal) {
    return inFlight ?? refusal;
  }
  return unacknowledged ? "Review and acknowledge the warnings above first" : undefined;
}

export function SubmissionDialog({
  open,
  record,
  submitting,
  refusal = undefined,
  error,
  onClose,
  onSubmit,
}: SubmissionDialogProps): React.ReactElement {
  const [note, setNote] = useState("");
  const warnings = record.submissionWarnings;
  // The acknowledgement names the warnings it was given for, so a changed
  // list is asked about again rather than carried through on an old tick.
  const warningsKey = warnings.map((entry) => `${entry.code}:${entry.message}`).join("\n");
  const [acknowledgedKey, setAcknowledgedKey] = useState<string | null>(null);
  const acknowledged = acknowledgedKey === warningsKey;
  const setAcknowledged = (checked: boolean) => setAcknowledgedKey(checked ? warningsKey : null);
  // Each opening is a new submission: a note and a tick from one that was
  // closed unsent do not belong to the next.
  // The last attempt's error belongs to it too, so an opening hides it.
  const [wasOpen, setWasOpen] = useState(open);
  const [staleError, setStaleError] = useState<Error | null>(null);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setNote("");
      setAcknowledgedKey(null);
      setStaleError(error);
    }
  }
  const shownError = error === staleError ? null : error;
  // The one screen where the reader has just pressed something and is waiting
  // to learn whether it worked, so a control refusing in silence reads as the
  // press having gone nowhere.
  const inFlight = submitting ? "Submitting the module" : undefined;

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeRefusal={inFlight}
      title="Submit module for review"
      size="lg"
      data-testid="module-submission-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting} disabledReason={inFlight}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon="paper-plane-tilt"
            loading={submitting}
            disabled={submitting || Boolean(refusal) || (warnings.length > 0 && !acknowledged)}
            disabledReason={confirmRefusal(inFlight, refusal, warnings.length > 0 && !acknowledged)}
            onClick={() => onSubmit({ note, warningsAcknowledged: acknowledged })}
            data-testid="module-confirm-submission"
          >
            Submit module
          </Button>
        </>
      }
    >
      <MutedText>
        Your Programme Lead will receive this module and the findings shown below.
      </MutedText>

      {warnings.length > 0 ? (
        <div className="mt-5">
          <Heading level={3} size="md">
            {countLabel(warnings.length, "warning")} to review
          </Heading>
          <ul className="mt-3 max-h-64 space-y-2 overflow-y-auto">
            {warnings.map((entry) => (
              <li key={entry.code}>
                <Alert tone="warning" role="none">
                  <div>
                    <Eyebrow as="div">{WARNING_SECTION_LABELS[entry.section]}</Eyebrow>
                    <Text as="div" className="mt-1">
                      {entry.message}
                    </Text>
                  </div>
                </Alert>
              </li>
            ))}
          </ul>
          <div className="mt-4">
            <Checkbox
              id="module-submission-warning-acknowledgement"
              checked={acknowledged}
              onChange={setAcknowledged}
              label="I have reviewed these warnings and want to submit the module with them recorded for the Programme Lead."
              data-testid="module-submission-warning-acknowledgement"
            />
          </div>
        </div>
      ) : (
        <Alert tone="success" className="mt-5">
          No advisory findings were identified for this module.
        </Alert>
      )}

      <div className="mt-5">
        <FormField
          label="Submission note"
          htmlFor="module-submission-note"
          helpText="Optional context for the Programme Lead reviewing this module."
        >
          <TextAreaField
            id="module-submission-note"
            value={note}
            onChange={setNote}
            disabled={submitting}
            disabledReason={inFlight}
          />
        </FormField>
      </div>

      {shownError ? (
        <Alert tone="warning" className="mt-4">
          {shownError.message}
        </Alert>
      ) : null}
    </Modal>
  );
}

export default SubmissionDialog;
